import CustomHtmlKeyboardResponsePlugin from "../../customPlugin/customHtmlKeyboardResponse";

import { KEY_CODES } from "../../gearshiftGame/constants";
import { Game } from "../../gearshiftGame/game";
import { Score } from "../../gearshiftGame/score";
import { createStimulus, hideElement } from "../../gearshiftGame/utils";
import {
  getGearshiftMissionConfig,
  getLayoutVideoUrl,
} from "../../lib/utils";
import { buildAchievementsTrial } from "../trials/achievements";
import {
  buildFeedbackTrial,
  buildQuizOneProcedure,
  buildQuizTwoProcedure,
} from "../trials/feedback";
import { buildInstructionsTrial } from "../trials/instructions";
import { tutorialToggles } from "../../config/tutorialToggles";
import { updateDebugMissionConfig } from "../ui/debugHelpers";
import { buildTutorialWithPhases } from "./utils/buildTutorialWithPhases";
import { buildBasicControlsTutorial } from "./utils/buildBasicControlsTutorial";
import { buildPracticeChoiceTrial, createPracticeChoiceChecker } from "./utils/repracticeUtils";
import { addFirstTaskMetadata } from "./utils/missionHelpers";
import {
  buildPhase0,
  buildPhase1,
  buildPhase2,
  buildPhase3,
  buildPhase4,
  buildPhase5,
  buildPhase7,
} from "./phases/missionOnePhases";
import { buildSkipMissionProcedure } from "./skipMission";
import {
  MISSION_ONE_PAGE_COUNT,
  buildMissionOneInstructionPages,
} from "./instructionBuilders/missionOne/buildMissionOneInstructionPages";
import { injectSurveyPanel, removeSurveyPanel } from "./hq/buildHQSurveyPanel.js";
import { injectHQMessage, removeHQMessage } from "./hq/buildHQMessage.js";
import { getHQMessagePanelConfig } from "./hq/localHQMessages.js";

/**
 * @type {number}
 * @description The current mission
 */
const MISSION_NUMBER = 1;

/**
 * @type {Score}
 * @description The user's score
 * NOTE: Used here so we don't have to pass it through every single function
 */
let SCORE;

/**
 * Global counter for tutorial failures in this mission
 */
let tutorialFailureCount = 0;

/**
 * Global failure limit for this mission
 */
let failureLimit = 1;

/**
 * Builds the first mission of the game
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @returns The timeline object for the first mission
 */
export async function buildMissionOne(jsPsych, metadata) {
  // Reset tutorial failure counter when entering this mission
  tutorialFailureCount = 0;

  // Check if a config file was given for this mission
  let config;
  try {
    config = await getGearshiftMissionConfig(jsPsych, metadata, MISSION_NUMBER);
  } catch (e) {
    console.error(`Unable to load mission config file for mission ${MISSION_NUMBER}`);
  }

  // No config file was given for mission 1, skip this mission (no file given or failed to load the file)
  if (!config) return buildSkipMissionProcedure(MISSION_NUMBER);

  // Config file given - build the mission!
  console.log("Mission One Configuration:", config);

  // Update debug overlay with mission configuration
  updateDebugMissionConfig(jsPsych, config);

  // Initialize variables shared across the mission
  const {
    missionLives,
    missionMinScore,
    missionMaxCrash,
    missionNumTrials,
    instructionsMaxRetries,
    instructionsAudio,
    trials,
    thisFile,
  } = config;
  // Get the tutorial settings for this mission
  const missionTutorials = {
    ...(tutorialToggles.mission1 ?? {}),
    ...(config.tutorials ?? {}),
    ...(instructionsAudio !== undefined ? { instructionsAudio } : {}),
  };
  // Get the failure limit from the mission config, fallback to tutorial toggles
  failureLimit = instructionsMaxRetries ?? missionTutorials.failsCountLimit ?? 1;

  SCORE = new Score(
    jsPsych,
    missionLives, // # of lives to start the mission with
    metadata.rewardReductionByReset // Amount to reduce score by when the user runs out of lives
  );

  // Build the timeline from the config's trials array
  const missionOneTimeline = trials.map((trialConfig) => {
    const stimulus = () => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig);
    switch (trialConfig.trialType) {
      case "INSTRUCTION":
        // NOTE: Instructions trial is the first trial of the mission
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionOneInstructions(jsPsych, trialConfig, metadata, stimulus(), trials, missionTutorials);
      case "TASK":
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionOneTask(jsPsych, metadata, trialConfig, stimulus);
      case "MESSAGE":
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionOneMessage(jsPsych, metadata, trialConfig, stimulus);
      case "SURVEY":
        return buildMissionOneSurvey(jsPsych, metadata, trialConfig, stimulus);
      case "FEEDBACK":
        // NOTE: Feed back trial is the last trial of the mission
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionOneFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash);
      default:
        throw new Error("An unknown trial type was found:", trialConfig.trialType);
    }
  });

  // Add task_metadata to the first TASK trial
  addFirstTaskMetadata(missionOneTimeline, trials, metadata);

  return {
    data: {
      missionNumber: MISSION_NUMBER,
      missionFile: thisFile,
      missionNumTrials,
      skipped: false,
    },
    save_trial_parameters: { stimulus: false },
    timeline: missionOneTimeline,
  };
}

/**
 * Builds the trial for displaying the instructions for mission one
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The configuration object for a specific trial
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} allTrials All trials from the mission config (to find a TASK trial for tutorial)
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionOneInstructions(jsPsych, trialConfig, metadata, stimulus, allTrials, missionTutorials = {}) {
  // Get audio enabled status from backend config, fallback to local configs
  const instructionAudioEnabled = missionTutorials.instructionsAudio ?? tutorialToggles.mission1?.instructionsAudio ?? true;
  const instructionAudio = (src) => (instructionAudioEnabled ? src : undefined);

  // find the first TASK trial to use since the tutorial config (the one called by INSTRUCTION parameters have empty arrays)
  const tutorialConfig = { ...(allTrials.find((t) => t.trialType === "TASK") || trialConfig) };
  const tutorialStimulus = createStimulus(MISSION_NUMBER, 1, tutorialConfig); // Tutorial shows 1/1 progress

  const pages = buildMissionOneInstructionPages(jsPsych, instructionAudio);
  const totalPages = MISSION_ONE_PAGE_COUNT;

  // first page, then the test (after the second page), then the remaining pages
  const timeline = [];
  const tutorialEnabled = (key, fallback = true) => missionTutorials[key] ?? fallback;

  // Check if we should ask user about practice
  const askRepractice = missionTutorials.askRepractice ?? true;

  // Helper function to check if user wants practice
  const checkPracticeChoice = createPracticeChoiceChecker(jsPsych, askRepractice);

  // Push the first 2 instruction pages (1.01 to 1.02)
  if (pages.length > 0) {
    const firstPages = pages.slice(0, Math.min(2, pages.length));
    timeline.push(
      buildInstructionsTrial(jsPsych, firstPages, {
        total_pages: totalPages,
        page_offset: 0,
      })
    );
  }

  // Add practice choice screen after page 1.02
  if (askRepractice) {
    timeline.push(buildPracticeChoiceTrial(jsPsych, "Mission 0"));
  }

  // Basic Driving Controls tutorials after page 1.02
  if (pages.length >= 2 && tutorialEnabled("decel")) {
    timeline.push({
      timeline: [
        buildBasicControlsTutorial(jsPsych, metadata, tutorialConfig, tutorialStimulus, 'decel', buildMissionOneTaskWithPhasesCustom)
      ],
      conditional_function: checkPracticeChoice,
    });
  }
  if (pages.length >= 2 && tutorialEnabled("accel")) {
    timeline.push({
      timeline: [
        buildBasicControlsTutorial(jsPsych, metadata, tutorialConfig, tutorialStimulus, 'accel', buildMissionOneTaskWithPhasesCustom)
      ],
      conditional_function: checkPracticeChoice,
    });
  }
  if (pages.length >= 2 && tutorialEnabled("reveal")) {
    timeline.push({
      timeline: [
        buildBasicControlsTutorial(jsPsych, metadata, tutorialConfig, tutorialStimulus, 'reveal', buildMissionOneTaskWithPhasesCustom)
      ],
      conditional_function: checkPracticeChoice,
    });
  }

  // Push instruction pages 1.03 to 1.05 (index 2 to 5)
  if (pages.length > 2) {
    const midPages = pages.slice(2, Math.min(5, pages.length));
    timeline.push(
      buildInstructionsTrial(jsPsych, midPages, {
        total_pages: totalPages,
        page_offset: 2,
      })
    );
  }

  // Full Lane tutorial after page 1.05
  if (pages.length >= 5 && tutorialEnabled("fullLane")) {
    timeline.push({
      timeline: [
        buildMissionOneTaskWithPhasesCustom(
          jsPsych,
          metadata,
          tutorialConfig,
          tutorialStimulus,
          [2, 3, 4, 5],
          {
            agentFile: "footer_LaneLabels.svg",
            tutorialHeader: {
              title: "Practicing Lane Selection (1/1)",
              instruction: "1. Decide on the Mafia Type<br/>2. Look at their Feature in the Code<br/>3. Crack the Code and Select the Lane"
            },
            ready: {
              imageOrVideoUrl: () => getLayoutVideoUrl("instructions/videos/m1/1_solve.mp4"),
            },
          }
        )
      ],
    });
  }

  // Push the remaining instruction pages starting from index 5 (1.06 onwards)
  if (pages.length > 5) {
    const remaining = pages.slice(5);
    timeline.push(
      buildInstructionsTrial(jsPsych, remaining, {
        total_pages: totalPages,
        page_offset: 5,
      })
    );
  }

  return {
    data: { trialConfig },
    timeline,
    on_finish: (data) => SCORE.assignScore(data),
  };
}

/**
 * Builds the trial needed for mission one's task
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionOneTask(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: newGame.targetLaunchDelay,
        on_load: () => {
          newGame.startPhase0();
        },
        on_finish: (data) => {
          data.name = "initializeTarget";
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = newGame.driverDistanceToTarget;
          data.trialStart = {
            name: "trialStart",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          };
          SCORE.assignScore(data);
        },
      },

      // Phase 1: Target car begins traveling towards driver car.
      // Terminates when target car/driver car lock in proximity zone
      {
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase1(),
        on_finish: (data) => {
          data.name = "engageTarget";
          data.pulseTTLtrialStart = newGame.ttlPulseFired ? 1 : 0;
          data.TTLpulse_time = newGame.ttlPulseTime;
          data.cueCarOn = {
            name: "cueCarOn",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
            distanceToTarget: newGame.driverDistanceToTarget,
            csi: newGame.csi,
          };

          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      // Phase 2: Start of CSI period, triggered by proximity zone initiation in previous phase
      // Terminates when CSI is over and it's time to show the license
      {
        choices: "NO_KEYS",
        trial_duration: newGame.csi,
        on_load: () => newGame.startPhase2(),
        on_finish: (data) => {
          data.name = "licensePlateOn";
          data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
          SCORE.assignScore(data);
        },
      },

      // Phase 3: At the start of this phase, the license is displayed
      // NOTE: The timeout for releaseMechanism 1 and 2 are handled in the Game, not here
      {
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase3(),
        on_finish: (data) => {
          data.name = "responseKeyPressed";
          data.response_keypress_time = data.time_elapsed;
          data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null; // keyCode is null if no response is given
          data.responseTime =
            data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          // displayLicense()/captureLicensePlateOnTime() only run once startPhase3's on_load
          // fires, which is after the "licensePlateOn" (phase 2) row already saved - so this
          // is the earliest row where the value is actually available
          data.license_plate_on_time = newGame.licensePlateOnTime;

          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      // Phase 4: Once lane has been chosen in phase 3, phase 4 begins with the driver car moving to the user chosen lane
      // Terminates when the driver car reaches the chosen lane.
      {
        choices: "NO_KEYS",
        // Start the phase based on the lane chosen
        on_load: () => newGame.startPhase4(jsPsych.data.get().last(1).values()[0].keyCode),
        on_finish: (data) => {
          data.name = "driverInResponseLane";
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },

      //Phase 5
      // Once driver is in chosen lane, the package will appear and travel down the correct lane. Simultaneously, the target car also moves down the highway until it disappears.
      // Once the package reaches the end of the highway, a score will show above the driver car, showing the points the driver car received in this game trial.
      {
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase5(),
        on_finish: (data) => {
          data.name = "packageDisappears";
          data.driverSpeed = newGame.driverCarSpeed;
          data.emotion = newGame.emotion;
          data.packageInTargetLane = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
            packageDistance: newGame.packageDistanceToTarget,
          };
          data.targetDisappears = {
            timestamp: newGame.targetCarDisappearTimestamp,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.emotionReaction = {
            emotion: newGame.emotion,
            driverSpeed: newGame.driverCarSpeed,
            timestamp:
              jsPsych.data.get().last(1).values()[0].time_elapsed - newGame.feedbackDuration,
          };

          SCORE.assignScore(data);
        },
      },

      // Phase 7: Driver car travels back to the center of the highway
      {
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase7(),
        on_finish: (data) => {
          data.name = "driverAtCenter";
          data.driverToCenter = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Build a test object that contains only the requested phases (example [1] for phase 1).
 */

function buildMissionOneTaskWithPhasesCustom(
  jsPsych,
  metadata,
  trialConfig,
  stimulus,
  phases = [1],
  options = {}
) {
  const buildersMap = {
    0: buildPhase0,
    1: buildPhase1,
    2: buildPhase2,
    3: buildPhase3,
    4: buildPhase4,
    5: buildPhase5,
    7: buildPhase7,
  };

  const { agentFile, tutorialHeader, ...customOptions } = options;

  // Recreate stimulus with tutorialHeader if provided
  let finalStimulus = stimulus;
  if (tutorialHeader) {
    const configForStimulus = {
      ...trialConfig,
      ...(agentFile ? { agentFile } : {}),
      tutorialHeader,
    };
    finalStimulus = createStimulus(
      MISSION_NUMBER,
      1,
      configForStimulus
    );
  }
  // Default overlay options
  const defaultOverlayOptions = {
    ready: {
      forwardText: `[press "__FORWARD_KEY__" twice to start]`,
      backText: `[press "__REPLAY_KEY__" to play video]`,
      imageOrVideoUrl: "assets/images/instructions/video_placeholder.svg",
      showText: false,
    },
    congrats: {
      forwardText: `[press "__FORWARD_KEY__" to continue] ->`,
      backText: "",
      imageOrVideoUrl: "assets/images/instructions/demo_congratulations.png",
      forwardTextCrash: `[press "__FORWARD_KEY__" to try again ]`,
      backTextCrash: "",
      imageOrVideoUrlCrash: "assets/images/instructions/demo_try_again.png",
      showText: false,
    },
  };

  // Merge custom overlays if provided
  const overlayOptions = {
    ready: { ...defaultOverlayOptions.ready, ...customOptions.ready },
    congrats: { ...defaultOverlayOptions.congrats, ...customOptions.congrats },
  };

  // Avoid mutating the shared trialConfig because it is reused by later tutorials
  const trialConfigWithOverrides = {
    ...trialConfig,
    ...(agentFile ? { agentFile } : {}),
    ...(customOptions.disableAcceleration !== undefined
      ? { disableAcceleration: customOptions.disableAcceleration }
      : {}),
    ...(customOptions.disableDeceleration !== undefined
      ? { disableDeceleration: customOptions.disableDeceleration }
      : {}),
    ...(customOptions.hideTargetCar !== undefined
      ? { hideTargetCar: customOptions.hideTargetCar }
      : {}),
    ...(customOptions.customTrialConfigOverrides || {}),
  };

  return buildTutorialWithPhases({
    jsPsych,
    metadata,
    score: SCORE,
    trialConfig: trialConfigWithOverrides,
    stimulus: finalStimulus,
    phases,
    buildersMap,
    overlayOptions,
    customOptions,
    hooks: {
      onBeforeCongratsOverlay: (data) => {
        if (data.tryAgain) {
          tutorialFailureCount++;
          console.log(
            `❌ Tutorial failed (attempt ${tutorialFailureCount}/${failureLimit})`
          );
          if (tutorialFailureCount >= failureLimit) {
            // Redirect to support page
            window.location.href = 'supportPage.html';
            return true; // Prevents overlay from showing
          }
        }
        return false;
      },
    },
  });
}

/**
 * Builds the trial needed to display the headquarter message
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionOneMessage(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);
  const hqConfig = getHQMessagePanelConfig(MISSION_NUMBER, trialConfig.agentFile);
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    choices: ["Space"],
    on_load: () => {
      newGame.startPhase6();
      if (hqConfig) injectHQMessage(jsPsych, hqConfig);
    },
    on_finish: (data) => {
      removeHQMessage();
      SCORE.assignScore(data);
    },
  };
}

/**
 * Builds the trial needed for mission one's survey
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @returns
 */
function buildMissionOneSurvey(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    timeline: [
      // Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: newGame.targetLaunchDelay,
        on_load: () => newGame.startPhase0(),
      },
      // Phase 6: Headquarter Survey
      {
        on_load: () => {
          newGame.startPhase(6);
          hideElement(newGame.targetCar);
          hideElement(newGame.agentImage);
          newGame.playEffect("message");
          injectSurveyPanel(jsPsych, {
            portrait: "hq-happy",
            promptText: "How are you?",
          });
        },
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        trial_duration: newGame.headquarterMessageDuration,
        on_finish: (data) => {
          removeSurveyPanel();
          data.name = "messageFromHQ";
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Builds the trial needed for mission one's feedback
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} missionMinScore Per mission minimum score to continue
 * @param {String} missionMaxCrash Per mission max crashes allowed
 * @returns
 */
function buildMissionOneFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash) {
  return {
    data: { trialConfig },
    timeline: [
      // Display the total rewards
      ...buildFeedbackTrial(
        jsPsych,
        SCORE,
        missionMinScore,
        missionMaxCrash,
        "assets/images/postgame/FeedbackScreen_Mission1.svg"
      ),
      // Display the first quiz
      buildQuizOneProcedure(jsPsych, SCORE),
      // Display the second quiz
      buildQuizTwoProcedure(jsPsych, SCORE),
      // Display the user's achievements
      buildAchievementsTrial("assets/images/postgame/achievementScreen_mission1.svg"),
    ],
  };
}
